import { useCallback, useEffect, useRef, useState } from "react";
import { Minus, Plus, Maximize2, Download, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface Props {
  src: string;
  alt?: string;
  /** Class applied to the outer viewport (controls sizing, e.g. `max-h-[60vh]`). */
  className?: string;
  /** Class applied to the <img> itself. */
  imgClassName?: string;
  maxScale?: number;
  minScale?: number;
  /** URL opened by "Open in new tab" — prefer a signed storage URL. Defaults to `src`. */
  openUrl?: string;
  /** URL used by "Download" — prefer a same-origin blob URL so the filename sticks. Defaults to openUrl/src. */
  downloadUrl?: string;
  /** Suggested file name for the download action. */
  fileName?: string;
  /** Hide the open/download actions (e.g. non-document imagery). */
  hideDownload?: boolean;
}

/**
 * Zoomable image viewer with wheel, pinch, drag-to-pan and double-tap zoom.
 * Pure CSS transform — no external deps.
 */
export function ZoomableImage({
  src,
  alt = "",
  className,
  imgClassName,
  maxScale = 6,
  minScale = 1,
  openUrl,
  downloadUrl,
  fileName,
  hideDownload = false,
}: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [scale, setScale] = useState(1);
  const [tx, setTx] = useState(0);
  const [ty, setTy] = useState(0);

  // Reset transform when the source changes.
  useEffect(() => {
    setScale(1);
    setTx(0);
    setTy(0);
  }, [src]);

  const clampPan = useCallback(
    (nextTx: number, nextTy: number, nextScale: number) => {
      const el = containerRef.current;
      if (!el) return { x: nextTx, y: nextTy };
      const { width, height } = el.getBoundingClientRect();
      const overflowX = Math.max(0, (width * nextScale - width) / 2);
      const overflowY = Math.max(0, (height * nextScale - height) / 2);
      return {
        x: Math.max(-overflowX, Math.min(overflowX, nextTx)),
        y: Math.max(-overflowY, Math.min(overflowY, nextTy)),
      };
    },
    [],
  );

  const applyZoom = useCallback(
    (nextScale: number, originClientX?: number, originClientY?: number) => {
      const el = containerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const clamped = Math.max(minScale, Math.min(maxScale, nextScale));
      const cx = originClientX ?? rect.left + rect.width / 2;
      const cy = originClientY ?? rect.top + rect.height / 2;
      // Anchor zoom on cursor: translate so that the point under cursor stays fixed.
      const dx = cx - (rect.left + rect.width / 2);
      const dy = cy - (rect.top + rect.height / 2);
      const ratio = clamped / scale;
      const nextTx = dx - (dx - tx) * ratio;
      const nextTy = dy - (dy - ty) * ratio;
      const { x, y } = clampPan(nextTx, nextTy, clamped);
      setScale(clamped);
      setTx(clamped === 1 ? 0 : x);
      setTy(clamped === 1 ? 0 : y);
    },
    [scale, tx, ty, clampPan, maxScale, minScale],
  );

  // Non-passive wheel handler so we can preventDefault.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15;
      applyZoom(scale * factor, e.clientX, e.clientY);
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [applyZoom, scale]);

  // Pointer tracking for pan + pinch.
  const pointers = useRef<Map<number, { x: number; y: number }>>(new Map());
  const pinchStart = useRef<{ dist: number; scale: number; cx: number; cy: number } | null>(null);
  const lastTap = useRef<number>(0);

  const onPointerDown = (e: React.PointerEvent) => {
    (e.target as Element).setPointerCapture?.(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2) {
      const [a, b] = Array.from(pointers.current.values());
      pinchStart.current = {
        dist: Math.hypot(a.x - b.x, a.y - b.y),
        scale,
        cx: (a.x + b.x) / 2,
        cy: (a.y + b.y) / 2,
      };
    }
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const prev = pointers.current.get(e.pointerId);
    if (!prev) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (pointers.current.size === 2 && pinchStart.current) {
      const [a, b] = Array.from(pointers.current.values());
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      const nextScale = pinchStart.current.scale * (dist / pinchStart.current.dist);
      applyZoom(nextScale, pinchStart.current.cx, pinchStart.current.cy);
      return;
    }

    if (pointers.current.size === 1 && scale > 1) {
      const dx = e.clientX - prev.x;
      const dy = e.clientY - prev.y;
      const { x, y } = clampPan(tx + dx, ty + dy, scale);
      setTx(x);
      setTy(y);
    }
  };

  const onPointerUp = (e: React.PointerEvent) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) pinchStart.current = null;
  };

  const onDoubleClick = (e: React.MouseEvent) => {
    if (scale > 1) applyZoom(1);
    else applyZoom(2.5, e.clientX, e.clientY);
  };

  // Handle double-tap on touch (Pointer Events don't fire dblclick reliably).
  const onPointerUpTap = (e: React.PointerEvent) => {
    if (e.pointerType !== "touch") return;
    const now = Date.now();
    if (now - lastTap.current < 300) {
      if (scale > 1) applyZoom(1);
      else applyZoom(2.5, e.clientX, e.clientY);
    }
    lastTap.current = now;
  };

  const zoomed = scale > 1;
  const viewUrl = openUrl || src;
  const saveUrl = downloadUrl || openUrl || src;
  const showActions = !hideDownload && !!viewUrl;

  return (
    <div
      ref={containerRef}
      className={cn(
        "relative overflow-hidden select-none",
        zoomed ? "cursor-grab active:cursor-grabbing" : "cursor-zoom-in",
        className,
      )}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={(e) => {
        onPointerUpTap(e);
        onPointerUp(e);
      }}
      onPointerCancel={onPointerUp}
      onDoubleClick={onDoubleClick}
    >
      <img
        src={src}
        alt={alt}
        draggable={false}
        style={{
          transform: `translate(${tx}px, ${ty}px) scale(${scale})`,
          transformOrigin: "center center",
          transition: pointers.current.size === 0 ? "transform 120ms ease-out" : "none",
          touchAction: "none",
        }}
        className={cn("w-full h-full object-contain mx-auto", imgClassName)}
      />
      <div className="absolute top-2 right-2 flex items-center gap-1 rounded-md bg-background/80 backdrop-blur-sm p-1 shadow-sm">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={(e) => {
            e.stopPropagation();
            applyZoom(scale / 1.4);
          }}
          aria-label="Zoom out"
        >
          <Minus className="h-3.5 w-3.5" />
        </Button>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            applyZoom(1);
          }}
          className="px-1.5 min-w-[3rem] text-xs tabular-nums text-foreground/80 hover:text-foreground"
          aria-label="Reset zoom"
        >
          {Math.round(scale * 100)}%
        </button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={(e) => {
            e.stopPropagation();
            applyZoom(scale * 1.4);
          }}
          aria-label="Zoom in"
        >
          <Plus className="h-3.5 w-3.5" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={(e) => {
            e.stopPropagation();
            applyZoom(1);
          }}
          aria-label="Fit to view"
        >
          <Maximize2 className="h-3.5 w-3.5" />
        </Button>
        {showActions && (
          <>
            <span className="mx-0.5 h-4 w-px bg-border" aria-hidden />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              asChild
              aria-label="Open in new tab"
              title="Open in new tab"
            >
              <a
                href={viewUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
              >
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              asChild
              aria-label="Download"
              title="Download"
            >
              <a
                href={saveUrl}
                download={fileName || ""}
                onClick={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
              >
                <Download className="h-3.5 w-3.5" />
              </a>
            </Button>
          </>
        )}
      </div>
    </div>
  );
}